import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md font-semibold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      variant: {
        // P1 (Primary) - Solid accent button with semantic hover/pressed states
        default: 'bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))] active:bg-[hsl(var(--primary-pressed))]',

        // P2 (Secondary) - Solid muted button
        secondary: 'bg-secondary text-secondary-foreground hover:bg-[hsl(var(--secondary-hover))] active:bg-[hsl(var(--secondary-pressed))]',

        // P2 (Outline) - Alternative secondary style
        outline: 'border-2 border-primary bg-transparent text-primary hover:bg-primary/10 active:bg-primary/20',

        // P3 (Ghost/Tertiary) - Minimal visual weight
        ghost: 'bg-transparent text-foreground hover:bg-accent/10 active:bg-accent/20',

        // Link - Text-only
        link: 'text-primary underline-offset-4 hover:underline',

        // Destructive - Use outline on general pages, solid in confirmation dialogs
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80',
        'destructive-outline': 'border-2 border-destructive bg-transparent text-destructive hover:bg-destructive/10 active:bg-destructive/20',
      },
      size: {
        default: 'h-11 min-h-[44px] px-6 py-2 text-base',  // Fitts's Law: ≥44px
        sm: 'h-10 min-h-[44px] px-4 text-sm',
        lg: 'h-12 min-h-[44px] px-8 text-lg',
        icon: 'h-11 w-11 min-h-[44px] min-w-[44px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  }
);
Button.displayName = 'Button';

export { Button };
